import dayjs from 'dayjs';
import {
  DaySequenceStatus,
  Job,
  ClassificationGroup,
  Classification,
} from '@core/generated/gql-types';
import {
  createFieldFilterConfig,
  type FilterConfig,
} from '@/components/filters';

export const getJobSiteLabel = (job: Job): string =>
  job.site?.name?.trim() || 'No site';

export type JobFilterConfig = FilterConfig<Job>;

export type JobFilterParams = {
  getClientById: (id: string) => { name?: string | null } | null;
  classificationGroups: ClassificationGroup[];
  getClassificationById: (id: string) => Classification | null;
};

export const getJobClassificationGroupName = (
  job: Job,
  params: Pick<
    JobFilterParams,
    'classificationGroups' | 'getClassificationById'
  >,
): string => {
  const classificationId = job.classification?.id ?? job.classificationId;
  if (!classificationId) return '-';
  const classification = params.getClassificationById(classificationId);
  if (!classification?.classificationGroupId) return '-';
  const group = params.classificationGroups.find(
    (g) => g.id === classification.classificationGroupId,
  );
  return group?.name || '-';
};

export const getJobClassificationName = (
  job: Job,
  params: Pick<JobFilterParams, 'getClassificationById'>,
): string => {
  const classificationId = job.classification?.id ?? job.classificationId;
  if (!classificationId) return '-';
  const classification = params.getClassificationById(classificationId);
  return classification?.name || '-';
};

const parseJobDateValue = (value: string | null | undefined) => {
  if (value == null || value === '') return null;
  const trimmed = value.trim();
  if (/^\d+$/.test(trimmed)) {
    const fromEpoch = dayjs(Number(trimmed));
    return fromEpoch.isValid() ? fromEpoch : null;
  }
  const parsed = dayjs(trimmed);
  return parsed.isValid() ? parsed : null;
};

/** True when the job’s scheduled span overlaps the inclusive calendar range (day granularity). */
export const doesJobOverlapDateRange = (
  job: Job,
  rangeStart: Date,
  rangeEnd: Date,
): boolean => {
  const windowStart = dayjs(rangeStart).startOf('day');
  const windowEnd = dayjs(rangeEnd).endOf('day');

  const jobStart =
    parseJobDateValue(job.startDate) ?? parseJobDateValue(job.createdAt);
  if (!jobStart) {
    return false;
  }

  const jobStartDay = jobStart.startOf('day');

  return (
    !jobStartDay.isAfter(windowEnd, 'day') &&
    !jobStartDay.isBefore(windowStart, 'day')
  );
};

const FIELD_FILTER_CONFIG = [
  createFieldFilterConfig<Job>('status', 'Status', 'itemStatus'),
  createFieldFilterConfig<Job>('workType', 'Work type', 'workType'),
];

export const JOB_APPLICATION_FILTER_HAS = 'Has application';
export const JOB_APPLICATION_FILTER_NONE = 'No application';

export const JOB_APPLICATION_FILTER_OPTIONS = [
  JOB_APPLICATION_FILTER_HAS,
  JOB_APPLICATION_FILTER_NONE,
] as const;

/** Normalised count for filters; null when not loaded or not a number. */
export const getJobApplicationCount = (job: Job): number | null => {
  const raw = job.applicationCount;
  if (raw == null) {
    return null;
  }
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : null;
};

export const getJobApplicationFilterValues = (job: Job): string[] => {
  const count = getJobApplicationCount(job);
  if (count == null) {
    return [];
  }
  if (count > 0) {
    return [JOB_APPLICATION_FILTER_HAS];
  }
  return [JOB_APPLICATION_FILTER_NONE];
};

export const jobMatchesApplicationFilters = (
  job: Job,
  selectedFilters: string[],
): boolean => {
  if (selectedFilters.length === 0) {
    return true;
  }
  const jobValues = getJobApplicationFilterValues(job);
  return jobValues.some((value) => selectedFilters.includes(value));
};

const DAY_SEQUENCE_STATUS_FILTER_OPTIONS = [
  DaySequenceStatus.InProgress,
  DaySequenceStatus.EscalatedToTenant,
  DaySequenceStatus.RequiresApproval,
  DaySequenceStatus.ProposedChange,
  DaySequenceStatus.ReadyForPay,
  DaySequenceStatus.PayProcessed,
].sort((a, b) => a.localeCompare(b));

export const createJobFilterConfig = (
  params: JobFilterParams,
): JobFilterConfig[] => {
  const { getClientById, classificationGroups, getClassificationById } = params;
  const classificationParams = {
    classificationGroups,
    getClassificationById,
  };

  const daySequenceStatusFilterConfig: JobFilterConfig = {
    key: 'daySequenceStatus',
    title: 'Timesheet status',
    getOptions: () => DAY_SEQUENCE_STATUS_FILTER_OPTIONS,
    getValueFromItem: () => [],
  };

  const clientConfig: JobFilterConfig = {
    key: 'client',
    title: 'Client',
    getOptions: (data: Job[]) =>
      Array.from(
        new Set(
          (data || [])
            .map((job) => getClientById(job.clientId ?? '')?.name)
            .filter((name): name is string => Boolean(name)),
        ),
      ).sort(),
    getValueFromItem: (job) => {
      const name = getClientById(job.clientId ?? '')?.name;
      return name ? [name] : [];
    },
  };

  const industryConfig: JobFilterConfig = {
    key: 'industry',
    title: 'Industry',
    getOptions: (data: Job[]) =>
      Array.from(
        new Set(
          (data || [])
            .map((job) =>
              getJobClassificationGroupName(job, classificationParams),
            )
            .filter((name) => name !== '-'),
        ),
      ).sort(),
    getValueFromItem: (job) => {
      const name = getJobClassificationGroupName(job, classificationParams);
      return name !== '-' ? [name] : [];
    },
  };

  const applicationConfig: JobFilterConfig = {
    key: 'application',
    title: 'Application',
    getOptions: () => [...JOB_APPLICATION_FILTER_OPTIONS],
    getValueFromItem: (job) => getJobApplicationFilterValues(job),
  };

  const skillSetConfig: JobFilterConfig = {
    key: 'skillSet',
    title: 'Skill set',
    getOptions: (data: Job[]) =>
      Array.from(
        new Set(
          (data || [])
            .map((job) =>
              getJobClassificationName(job, { getClassificationById }),
            )
            .filter((name) => name !== '-'),
        ),
      ).sort(),
    getValueFromItem: (job) => {
      const name = getJobClassificationName(job, { getClassificationById });
      return name !== '-' ? [name] : [];
    },
  };

  const siteConfig: JobFilterConfig = {
    key: 'site',
    title: 'Site',
    getOptions: (data: Job[]) =>
      Array.from(new Set((data || []).map(getJobSiteLabel))).sort(),
    getValueFromItem: (job) => [getJobSiteLabel(job)],
  };

  return [
    ...FIELD_FILTER_CONFIG,
    clientConfig,
    siteConfig,
    industryConfig,
    skillSetConfig,
    applicationConfig,
    daySequenceStatusFilterConfig,
  ];
};

export const JOB_FILTER_KEYS = [
  ...FIELD_FILTER_CONFIG.map((f) => f.key),
  'client',
  'site',
  'industry',
  'skillSet',
  'application',
  'daySequenceStatus',
] as const;
